import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Mail, 
  Lock, 
  UserCircle, 
  Briefcase, 
  Loader2, 
  ShieldCheck, 
  CheckSquare, 
  Square,
  ArrowLeft
} from 'lucide-react';

export default function RegisterPage() {
  const { t } = useTranslation();
  
  const AVAILABLE_PERMISSIONS = [
    { id: 'menu_categories', label: t('admin_nav_categories') },
    { id: 'menu_items', label: t('admin_nav_menu') },
    { id: 'gallery_categories', label: t('admin_nav_gallery_categories') },
    { id: 'gallery_photos', label: t('admin_nav_gallery') },
    { id: 'staff_management', label: t('admin_nav_users') },
  ];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee',
  });
  const [permissions, setPermissions] = useState<Record<string, boolean>>({
    menu_categories: false,
    menu_items: false,
    gallery_categories: false,
    gallery_photos: false,
    staff_management: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const togglePermission = (id: string) => {
    setPermissions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleRoleChange = (role: string) => {
    setFormData({ ...formData, role });
    if (role === 'administrator') {
      setPermissions({
        menu_categories: true,
        menu_items: true,
        gallery_categories: true,
        gallery_photos: true,
        staff_management: true,
      });
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        permissions: permissions,
        createdAt: new Date().toISOString(),
      });

      navigate('/admin/users');
    } catch (err: any) {
      console.error("Registration error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/admin/users')}
          className="w-10 h-10 flex items-center justify-center bg-[#111] hover:bg-[#F57F25] text-white hover:text-black rounded-xl transition-all border border-white/5"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_register_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_register_sub')}</p>
        </div>
      </div>

      <div className="bg-[#111] border border-white/5 rounded-[40px] overflow-hidden shadow-2xl">
        <form onSubmit={handleRegister} className="p-8 md:p-12 space-y-10">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs py-4 px-6 rounded-2xl text-center font-bold italic">
              {error}
            </div>
          )}

          {/* Identity Section */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <UserCircle className="text-[#F57F25]" size={20} />
              <h3 className="text-white font-black text-sm uppercase tracking-widest">{t('admin_register_personal')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_register_firstname')}</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
                    placeholder="e.g. Carlos"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_register_lastname')}</label>
                <div className="relative">
                  <UserCircle className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
                    placeholder="e.g. Rodriguez"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Credentials Section */}
          <div className="space-y-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="text-[#F57F25]" size={20} />
              <h3 className="text-white font-black text-sm uppercase tracking-widest">{t('admin_register_credentials')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_register_email')}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
                    placeholder="email@bucara.com"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_register_password')}</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Role & Permissions Section */}
          <div className="space-y-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="text-[#F57F25]" size={20} />
              <h3 className="text-white font-black text-sm uppercase tracking-widest">{t('admin_register_role_perm')}</h3>
            </div>
            
            <div className="space-y-2 max-w-md">
              <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_register_main_role')}</label>
              <select
                value={formData.role}
                onChange={e => handleRoleChange(e.target.value)}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="employee">{t('admin_register_staff_member')}</option>
                <option value="administrator">{t('admin_register_sys_admin')}</option>
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {AVAILABLE_PERMISSIONS.map(perm => (
                <button
                  key={perm.id}
                  type="button"
                  onClick={() => togglePermission(perm.id)}
                  className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                    permissions[perm.id]
                      ? 'bg-[#F57F25]/10 border-[#F57F25] text-[#F57F25]'
                      : 'bg-black border-white/5 text-white/40 hover:border-white/20'
                  }`}
                >
                  <span className="text-[10px] font-black uppercase tracking-tight">{perm.label}</span>
                  {permissions[perm.id] ? <CheckSquare size={18} /> : <Square size={18} />}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black text-sm uppercase py-6 rounded-3xl transition-all shadow-xl shadow-[#F57F25]/20 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={24} /> : <><ShieldCheck size={24} /> {t('admin_register_btn')}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
